/**
 * Leviathan Web - Accept Invitation Page
 * Quasar Prime LLC
 * Tactical Industrial Authentication Interface
 *
 * The landing page for the URL an admin hands out from Settings → Users →
 * Invite. Public by necessity: the invitee has no account until this form is
 * submitted.
 *
 * This page did not exist. Invitations generated a link to /accept-invite and
 * the router had no such route, so every invitation dead-ended on a blank
 * screen. Since public registration has been removed, invitations are now the
 * ONLY way to add a user to an instance — which made this the difference
 * between a deployable product and a one-account one.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Loader2, Mail, Terminal, UserPlus } from 'lucide-react';
import { API_URL } from '@/config/api';

interface InvitationPreview {
  email: string;
  role: string;
  organization: string;
  expiresAt: string;
}

/** Mirrors shared/utils/passwordPolicy.ts on the API. */
const PASSWORD_RULES = [
  { label: 'At least 12 characters', test: (p: string) => p.length >= 12 },
  { label: 'A lowercase letter', test: (p: string) => /[a-z]/.test(p) },
  { label: 'An uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  { label: 'A digit', test: (p: string) => /[0-9]/.test(p) },
];

export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // The invitation itself, resolved from the token before anything is typed.
  //
  // This page used to ask for a name and a password without ever saying which
  // email address the account was for. A real user came out the other side not
  // knowing which identity they had created, then could not sign in and had no
  // way to work out why. The address is the one thing on this form the user
  // does not choose, so it is the one thing that has to be shown to them.
  const [invitation, setInvitation] = useState<InvitationPreview | null>(null);
  const [previewError, setPreviewError] = useState('');
  const [isPreviewLoading, setIsPreviewLoading] = useState(!!token);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    (async () => {
      try {
        const response = await fetch(
          `${API_URL}/api/settings/invitations/preview?token=${encodeURIComponent(token)}`
        );
        const data = await response.json().catch(() => ({}));
        if (cancelled) return;

        if (!response.ok) {
          setPreviewError(
            data?.error?.message || data?.error || 'This invitation link could not be read.'
          );
          return;
        }

        setInvitation(data.invitation);
      } catch {
        if (!cancelled) {
          setPreviewError('Could not reach the server to check this invitation.');
        }
      } finally {
        if (!cancelled) setIsPreviewLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const rulesMet = PASSWORD_RULES.map((r) => r.test(password));
  const passwordOk = rulesMet.every(Boolean);
  // Gated on `invitation` as well: an expired, used, or unknown token is worth
  // saying so before someone fills the form in and has it rejected at the end.
  const canSubmit =
    !!token && !!invitation && !!firstName && !!lastName && passwordOk && password === confirm;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/settings/invitations/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, firstName, lastName, password }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        // The API reports validation failures as a list of issues and
        // everything else as a single `error` string.
        const issues = data?.error?.details?.issues as { message: string }[] | undefined;
        setError(
          issues?.map((i) => i.message).join(' ') ||
            data?.error?.message ||
            data?.error ||
            'Could not accept this invitation.'
        );
        return;
      }

      setDone(true);
      // The API creates the account but does not sign the invitee in, so send
      // them to the login screen rather than pretending they have a session.
      // The address travels with them and pre-fills the email field: the
      // surest way for someone not to sign in under the wrong address is for
      // them never to have to retype it.
      const signInAs = data?.user?.email ?? invitation?.email ?? '';
      setTimeout(
        () => navigate(signInAs ? `/login?email=${encodeURIComponent(signInAs)}` : '/login'),
        3500
      );
    } catch {
      setError('Could not reach the server. Check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-base-dark bg-grid-pattern relative overflow-hidden p-4">
      <div className="fixed inset-0 pointer-events-none z-0 opacity-20">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-amber/5 to-transparent animate-scan" />
      </div>

      <div className="fixed top-4 left-4 w-24 h-24 border-l-2 border-t-2 border-amber/30 pointer-events-none" />
      <div className="fixed top-4 right-4 w-24 h-24 border-r-2 border-t-2 border-amber/30 pointer-events-none" />
      <div className="fixed bottom-4 left-4 w-24 h-24 border-l-2 border-b-2 border-amber/30 pointer-events-none" />
      <div className="fixed bottom-4 right-4 w-24 h-24 border-r-2 border-b-2 border-amber/30 pointer-events-none" />

      <div className="w-full max-w-lg relative z-10">
        <div className="relative stat-card">
          <div className="absolute top-0 left-0 w-6 h-6 border-l-2 border-t-2 border-amber" />
          <div className="absolute top-0 right-0 w-6 h-6 border-r-2 border-t-2 border-amber" />
          <div className="absolute bottom-0 left-0 w-6 h-6 border-l-2 border-b-2 border-amber" />
          <div className="absolute bottom-0 right-0 w-6 h-6 border-r-2 border-b-2 border-amber" />

          <div className="bg-panel-dark border border-panel-border shadow-panel clip-path-octagon p-10 relative">
            <div className="absolute inset-0 shadow-[inset_0_2px_12px_rgba(0,0,0,0.6)] pointer-events-none" />

            <div className="mb-8 text-center relative">
              <div className="flex items-center justify-center space-x-4 mb-4">
                <div className="w-4 h-4 bg-amber rotate-45" style={{ boxShadow: '0 0 12px rgba(255,149,0,0.8)' }} />
                <h1
                  className="text-4xl font-display font-bold text-steel tracking-wider uppercase"
                  style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.8), 0 0 40px rgba(92, 140, 199, 0.4)' }}
                >
                  LEVIATHAN
                </h1>
                <div className="w-4 h-4 bg-amber rotate-45" style={{ boxShadow: '0 0 12px rgba(255,149,0,0.8)' }} />
              </div>

              <div
                className="h-px bg-gradient-to-r from-transparent via-amber to-transparent mb-4"
                style={{ boxShadow: '0 0 8px rgba(255,149,0,0.6)' }}
              />

              <div className="flex items-center justify-center space-x-4 text-xs font-mono">
                <Terminal className="w-4 h-4 text-steel" />
                <span className="text-text-secondary uppercase tracking-widest">Account Enrolment</span>
                <Terminal className="w-4 h-4 text-steel" />
              </div>
            </div>

            {!token && (
              <div className="mb-6 p-4 bg-error-red/10 border border-error-red/50 clip-path-octagon">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-error-red flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-error-red font-mono uppercase tracking-wider font-bold mb-1">
                      No Invitation Token
                    </p>
                    <p className="text-xs text-text-tertiary font-mono">
                      Open the full invitation link your administrator sent you. It ends with
                      <span className="text-amber"> ?token=…</span>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {token && isPreviewLoading && (
              <div className="mb-6 flex items-center justify-center space-x-3 p-4 border border-panel-border">
                <Loader2 className="w-4 h-4 text-amber animate-spin" />
                <span className="text-xs text-text-tertiary font-mono uppercase tracking-wider">
                  Checking invitation…
                </span>
              </div>
            )}

            {previewError && (
              <div className="mb-6 p-4 bg-error-red/10 border border-error-red/50 clip-path-octagon">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-error-red flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-error-red font-mono uppercase tracking-wider font-bold mb-1">
                      Invitation Unavailable
                    </p>
                    <p className="text-xs text-text-tertiary font-mono">{previewError}</p>
                  </div>
                </div>
              </div>
            )}

            {/* The identity being created. Read-only and above the form, so it
                is seen before a password is chosen rather than discovered at
                the sign-in screen afterwards. */}
            {invitation && !done && (
              <div className="mb-6 p-4 bg-amber/5 border border-amber/40 clip-path-octagon">
                <div className="flex items-start space-x-3">
                  <Mail className="w-5 h-5 text-amber flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] text-text-tertiary font-mono uppercase tracking-[0.2em] mb-1">
                      You are creating the account for
                    </p>
                    <p className="text-base text-amber font-mono font-bold break-all leading-snug">
                      {invitation.email}
                    </p>
                    <p className="mt-2 text-[11px] text-text-tertiary font-mono">
                      This is the address you will sign in with. It cannot be changed here — if it
                      is wrong, ask your administrator for a new invitation.
                    </p>
                    <div className="mt-3 pt-3 border-t border-amber/20 flex flex-wrap gap-x-6 gap-y-1 text-[11px] font-mono">
                      <span className="text-text-tertiary">
                        Organization:{' '}
                        <span className="text-text-secondary">{invitation.organization}</span>
                      </span>
                      <span className="text-text-tertiary">
                        Access level: <span className="text-text-secondary">{invitation.role}</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {done && (
              <div className="mb-6 p-4 bg-success-green/10 border border-success-green/50 clip-path-octagon">
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-success-green flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-success-green font-mono uppercase tracking-wider font-bold mb-1">
                      Account Created
                    </p>
                    {/* Repeated on the way out, because this is the moment the
                        address has to stick: the next screen asks for it. */}
                    <p className="text-xs text-text-tertiary font-mono">
                      Sign in with{' '}
                      <span className="text-amber font-bold break-all">{invitation?.email}</span>{' '}
                      and the password you just chose.
                    </p>
                    <p className="mt-1 text-xs text-text-tertiary font-mono">
                      Taking you to the sign-in screen…
                    </p>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="mb-6 p-4 bg-error-red/10 border border-error-red/50 clip-path-octagon">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-error-red flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-error-red font-mono uppercase tracking-wider font-bold mb-1">
                      Enrolment Failed
                    </p>
                    <p className="text-xs text-text-tertiary font-mono">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* No form until the invitation resolves. Filling in a name and a
                password against a dead token only to be rejected at submit is
                the failure this page is meant to stop. */}
            {!done && invitation && (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="firstName"
                      className="block text-[11px] font-mono text-text-secondary mb-3 uppercase tracking-[0.2em] flex items-center space-x-2"
                    >
                      <div className="w-1 h-4 bg-gradient-to-b from-amber to-transparent" />
                      <span>First Name</span>
                    </label>
                    <input
                      id="firstName"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      className="input font-mono text-sm"
                      placeholder="Dana"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="lastName"
                      className="block text-[11px] font-mono text-text-secondary mb-3 uppercase tracking-[0.2em] flex items-center space-x-2"
                    >
                      <div className="w-1 h-4 bg-gradient-to-b from-amber to-transparent" />
                      <span>Last Name</span>
                    </label>
                    <input
                      id="lastName"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      className="input font-mono text-sm"
                      placeholder="Okafor"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="block text-[11px] font-mono text-text-secondary mb-3 uppercase tracking-[0.2em] flex items-center space-x-2"
                  >
                    <div className="w-1 h-4 bg-gradient-to-b from-amber to-transparent" />
                    <span>Choose Access Code</span>
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="input font-mono text-sm tracking-widest"
                    placeholder="••••••••••••"
                  />

                  {/* Live policy feedback, so the rules are visible before
                      submitting rather than arriving as a server error. */}
                  <ul className="mt-3 space-y-1">
                    {PASSWORD_RULES.map((rule, i) => (
                      <li
                        key={rule.label}
                        className={`text-[10px] font-mono flex items-center space-x-2 ${
                          rulesMet[i] ? 'text-success-green' : 'text-text-tertiary'
                        }`}
                      >
                        <span className={`w-1.5 h-1.5 ${rulesMet[i] ? 'bg-success-green' : 'bg-text-tertiary/40'}`} />
                        <span>{rule.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <label
                    htmlFor="confirm"
                    className="block text-[11px] font-mono text-text-secondary mb-3 uppercase tracking-[0.2em] flex items-center space-x-2"
                  >
                    <div className="w-1 h-4 bg-gradient-to-b from-amber to-transparent" />
                    <span>Confirm Access Code</span>
                  </label>
                  <input
                    id="confirm"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    className="input font-mono text-sm tracking-widest"
                    placeholder="••••••••••••"
                  />
                  {confirm.length > 0 && confirm !== password && (
                    <p className="mt-2 text-[10px] font-mono text-error-red">Passwords do not match.</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading || !canSubmit}
                  className="btn btn-primary w-full py-3 text-base mt-6 relative group disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <span className="relative z-10 flex items-center justify-center space-x-2">
                    <UserPlus className="w-4 h-4" />
                    <span>{isLoading ? 'CREATING ACCOUNT...' : 'CREATE ACCOUNT'}</span>
                  </span>
                </button>
              </form>
            )}

            <div className="mt-8 pt-6 border-t border-panel-border">
              <p className="text-center text-text-tertiary text-sm font-mono">
                Already enrolled?{' '}
                <Link to="/login" className="text-amber hover:text-amber/80 transition-colors font-semibold">
                  Sign In
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
